if ( typeof VideoPopup !== 'function' ) {

  class VideoPopup extends HTMLElement {

    constructor(){

      super();

      this.trigger = this.querySelector('[data-js-video-popup-link]');
      this.container = this.querySelector('[data-js-video-popup-container]');
      this.closeButton = this.querySelector('[data-js-video-popup-close]');
      this.isProductMedia = this.classList.contains('video-popup--product-media');

      if ( ! this.trigger || ! this.container ) return;

      this.open = this.open.bind(this);
      this.close = this.close.bind(this);
      this.prepareMedia = this.prepareMedia.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleMediaWaiting = this.handleMediaWaiting.bind(this);
      this.handleMediaReady = this.handleMediaReady.bind(this);

      this.trigger.addEventListener('click', this.open);
      if ( this.closeButton ) this.closeButton.addEventListener('click', this.close);

      if ( this.isProductMedia ) {
        this.trigger.addEventListener('pointerenter', this.prepareMedia, { once: true });
        this.trigger.addEventListener('focus', this.prepareMedia, { once: true });
        this.trigger.addEventListener('touchstart', this.prepareMedia, { once: true, passive: true });
      }

    }

    prepareMedia(e){

      if ( this.activeMedia ) return this.activeMedia;

      const template = this.querySelector('template');
      if ( ! template || ! template.content.firstElementChild ) return null;
      if ( e && ! template.content.firstElementChild.matches('video') ) return null;

      this.activeMedia = template.content.firstElementChild.cloneNode(true);
      this.activeMedia.classList.add('video-popup__media');
      this.container.appendChild(this.activeMedia);

      if ( this.activeMedia.matches('video') ) {
        this.activeMedia.preload = 'metadata';
        this.activeMedia.addEventListener('waiting', this.handleMediaWaiting);
        this.activeMedia.addEventListener('playing', this.handleMediaReady);
        this.activeMedia.addEventListener('canplay', this.handleMediaReady);
      }

      return this.activeMedia;

    }

    open(e){

      e.preventDefault();
      if ( this.opened || this.closing || ! this.prepareMedia() ) return;

      this.opened = true;
      this.previouslyFocused = document.activeElement;
      this.mediaRoot = this.closest('[data-video]');
      this.pauseGalleryAutoplay();

      if ( this.isProductMedia ) {
        this.placeholder = document.createComment('video-popup-placeholder');
        this.parentNode.insertBefore(this.placeholder, this);
        document.body.appendChild(this);
      }

      this.blackout = document.createElement('div');
      this.blackout.classList.add('video-popup__blackout');
      this.blackout.addEventListener('click', this.close);
      this.append(this.blackout);

      this.classList.add('video-opened');
      this.setAttribute('role', 'dialog');
      this.setAttribute('aria-modal', 'true');
      const dialogLabel = this.trigger.getAttribute('aria-label');
      if ( dialogLabel ) this.setAttribute('aria-label', dialogLabel);
      this.trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('video-popup-open');
      document.addEventListener('keydown', this.handleKeydown);

      requestAnimationFrame(()=>{
        this.classList.add('video-visible');
        if ( this.blackout ) this.blackout.style.opacity = '1';
      });

      this._playMedia(this.mediaRoot);

      if ( this.closeButton ) this.closeButton.focus({ preventScroll: true });

    }

    close(){

      if ( ! this.opened || this.closing ) return;

      this.closing = true;
      this._pauseMedia();
      this.classList.remove('video-visible', 'video-buffering');
      if ( this.blackout ) this.blackout.style.opacity = '0';

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.setTimeout(()=>this.cleanup(), this.isProductMedia && ! reduceMotion ? 180 : 0);

    }

    cleanup(){

      if ( ! this.opened ) return;

      this.opened = false;
      this.closing = false;

      const video = this.querySelector('video');
      if ( video ) {
        video.pause();
        try {
          video.currentTime = 0;
        } catch ( error ) {
          // The browser can reject seeking before metadata is available.
        }
      }

      if ( ! this.isProductMedia || this.activeMedia?.matches('iframe') ) {
        this.activeMedia?.remove();
        this.activeMedia = null;
      }

      if ( this.blackout ) this.blackout.remove();

      this.classList.remove('video-opened');
      this.removeAttribute('role');
      this.removeAttribute('aria-modal');
      this.removeAttribute('aria-label');
      this.trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', this.handleKeydown);

      if ( ! document.querySelector('.video-popup.video-opened') ) {
        document.body.classList.remove('video-popup-open');
      }

      this.resumeGalleryAutoplay();

      if ( this.placeholder && this.placeholder.parentNode ) {
        this.placeholder.parentNode.insertBefore(this, this.placeholder);
        this.placeholder.remove();
      }

      if ( this.previouslyFocused && this.previouslyFocused.focus ) {
        this.previouslyFocused.focus({ preventScroll: true });
      }

      this.blackout = null;
      this.mediaRoot = null;
      this.placeholder = null;
      this.previouslyFocused = null;

    }

    handleMediaWaiting(){

      if ( this.opened ) this.classList.add('video-buffering');

    }

    handleMediaReady(){

      this.classList.remove('video-buffering');

    }

    handleKeydown(e){

      if ( e.key === 'Escape' ) {
        this.close();
        return;
      }

      if ( e.key !== 'Tab' ) return;

      const focusable = [
        this.closeButton,
        ...this.querySelectorAll('video[controls], iframe')
      ].filter(Boolean);
      if ( focusable.length === 0 ) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if ( e.shiftKey && document.activeElement === first ) {
        e.preventDefault();
        last.focus();
      } else if ( ! e.shiftKey && document.activeElement === last ) {
        e.preventDefault();
        first.focus();
      }

    }

    pauseGalleryAutoplay(){

      this.gallerySlider = this.closest('product-page')?.querySelector('css-slider');
      if ( ! this.gallerySlider || ! this.gallerySlider.o ) return;

      const autoplay = parseInt(this.gallerySlider.o.autoplay);
      if ( autoplay <= 0 ) return;

      this.galleryAutoplay = this.gallerySlider.o.autoplay;
      this.gallerySlider.o.autoplay = 0;
      if ( this.gallerySlider._autoplayInterval ) {
        clearInterval(this.gallerySlider._autoplayInterval);
      }

    }

    resumeGalleryAutoplay(){

      if ( this.gallerySlider && this.galleryAutoplay ) {
        this.gallerySlider.o.autoplay = this.galleryAutoplay;
        if ( this.gallerySlider._initAutoplay ) this.gallerySlider._initAutoplay();
      }

      this.gallerySlider = null;
      this.galleryAutoplay = null;

    }

    _pauseMedia(){

      this.querySelectorAll('.js-youtube').forEach(video=>{
        video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      });
      this.querySelectorAll('.js-vimeo').forEach(video=>{
        video.contentWindow.postMessage('{"method":"pause"}', '*');
      });
      this.querySelectorAll('video').forEach(video=>video.pause());

    }

    _playMedia(media){

      if ( ! media ) return;

      switch ( media.dataset.productMediaType ) {
        case 'video': {
          const video = this.querySelector('video');
          if ( video ) {
            video.preload = 'auto';
            if ( video.readyState < 3 ) this.classList.add('video-buffering');
            const playPromise = video.play();
            if ( playPromise ) playPromise.catch(()=>this.classList.remove('video-buffering'));
          }
          break;
        }
        case 'external_video-youtube':
          if ( this.querySelector('.js-youtube') ) {
            this.querySelector('.js-youtube').contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
          }
          break;
        case 'external_video-vimeo':
          if ( this.querySelector('.js-vimeo') ) {
            this.querySelector('.js-vimeo').contentWindow.postMessage('{"method":"play"}', '*');
          }
          break;
      }

    }

  }

  if ( typeof customElements.get('video-popup') == 'undefined' ) {
    customElements.define('video-popup', VideoPopup);
  }

}
